<template>
  <div class="dgc" :class="[cellClass, { 'dgc-total': isTotal }]">
    <!-- Línea 1: producción de meta  badge% -->
    <div class="dgc-row dgc-row-main">
      <span class="dgc-val" :class="valColorClass">{{ prod }}</span>
      <span v-if="hasMeta" class="dgc-meta-text">
        <span class="dgc-de">de</span>
        <span class="dgc-meta-num">{{ meta }}</span>
      </span>
      <span v-if="hasMeta" class="dgc-pct" :class="pctClass">{{ pctLabel }}</span>
      <span v-else class="dgc-pct dgc-pct-nometa">—</span>
    </div>

    <!-- Línea 2: barra de progreso -->
    <div class="dgc-bar" :title="barTitle">
      <div class="dgc-bar-fill" :style="{ width: barWidth + '%' }"></div>
    </div>

    <!-- Línea 3: nota -->
    <div class="dgc-note">
      <span v-if="!hasMeta" class="dgc-note-nometa">Sin meta</span>
      <span v-else-if="overMeta" class="dgc-note-real">Real: {{ Math.round(pctNum) }}%</span>
      <span v-else-if="pctNum >= 100" class="dgc-note-ok">Meta cumplida ✓</span>
      <span v-else-if="pctNum >= 85" class="dgc-note-close">¡Casi listo!</span>
      <span v-else class="dgc-note-sp">&nbsp;</span>
    </div>
  </div>
</template>

<script>
export default {
  name: 'DesgloseCell',
  props: {
    prod:   { type: [Number, null], default: 0 },
    meta:   { type: [Number, null], default: 0 },
    type:   { type: String, default: 'spots' },   // 'spots' | 'ia'
    isTotal: { type: Boolean, default: false },
  },
  computed: {
    hasMeta()   { return this.meta != null && this.meta > 0; },
    overMeta()  { return this.hasMeta && this.prod > this.meta; },
    pctNum()    { return this.hasMeta ? Math.min(999, (this.prod / this.meta) * 100) : null; },
    pctLabel()  { return this.pctNum != null ? Math.min(100, Math.round(this.pctNum)) + '%' : '—'; },
    barWidth()  { return this.pctNum != null ? Math.min(100, this.pctNum) : 0; },

    barTitle() {
      if (!this.hasMeta) return `Sin meta · producción: ${this.prod}`;
      return `Cumplimiento: ${Math.round(this.pctNum)}% (${this.prod} de ${this.meta})`;
    },

    pctClass() {
      if (!this.hasMeta) return 'dgc-pct-nometa';
      if (this.pctNum >= 100) return 'dgc-pct-green';
      if (this.pctNum >= 85)  return 'dgc-pct-yellow';
      return 'dgc-pct-red';
    },

    cellClass() {
      if (!this.hasMeta) return 'dgc-nometa';
      if (this.pctNum >= 100) return 'dgc-green';
      if (this.pctNum >= 85)  return 'dgc-yellow';
      return 'dgc-red';
    },

    valColorClass() {
      return this.type === 'ia' ? 'dgc-val-ia' : 'dgc-val-h';
    },
  },
};
</script>

<style scoped>
/* ── Base ── */
.dgc {
  display: flex;
  flex-direction: column;
  gap: 5px;
  padding: 8px 12px;
  font-variant-numeric: tabular-nums;
  font-family: 'Poppins', 'Roboto', sans-serif;
  line-height: 1.35;
  min-width: 140px;
}

/* ── Row 1: valor ── */
.dgc-row {
  display: flex;
  align-items: baseline;
  gap: 6px;
  font-size: 0.88rem;
}
.dgc-val {
  font-weight: 700;
  font-size: 1rem;
  letter-spacing: -0.01em;
}
.dgc-val-h  { color: #047857; }           /* emerald-700 */
.dgc-val-ia { color: #6d28d9; }           /* violet-700  */

.dgc-meta-text {
  display: inline-flex;
  align-items: baseline;
  gap: 3px;
  color: #94a3b8;
  font-size: 0.8rem;
}
.dgc-de {
  font-size: 0.72rem;
  color: #cbd5e1;
  font-weight: 400;
}
.dgc-meta-num {
  font-weight: 600;
  color: #64748b;
}

/* ── Pct badge ── */
.dgc-pct {
  margin-left: auto;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  white-space: nowrap;
}
.dgc-pct-green   { background: rgba(16, 185, 129, 0.15); color: #047857; }
.dgc-pct-yellow  { background: rgba(245, 158, 11, 0.15); color: #b45309; }
.dgc-pct-red     { background: rgba(239, 68, 68, 0.15);  color: #b91c1c; }
.dgc-pct-nometa  { background: rgba(148, 163, 184, 0.18); color: #64748b; }

/* ── Bar ── */
.dgc-bar {
  position: relative;
  height: 6px;
  background: rgba(148, 163, 184, 0.22);
  border-radius: 3px;
  overflow: hidden;
}
.dgc-bar-fill {
  position: absolute;
  left: 0; top: 0; bottom: 0;
  border-radius: 3px;
  transition: width 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}
.dgc-green  .dgc-bar-fill { background: linear-gradient(90deg, #34d399, #10b981); }
.dgc-yellow .dgc-bar-fill { background: linear-gradient(90deg, #fbbf24, #f59e0b); }
.dgc-red    .dgc-bar-fill { background: linear-gradient(90deg, #f87171, #ef4444); }
.dgc-nometa .dgc-bar-fill { background: rgba(148, 163, 184, 0.4); }

/* ── Note line ── */
.dgc-note {
  min-height: 15px;
  font-size: 0.68rem;
  font-weight: 500;
  white-space: nowrap;
}
.dgc-note-nometa { color: #94a3b8; font-style: italic; }
.dgc-note-real   { color: #64748b; font-weight: 600; }
.dgc-note-ok     { color: #10b981; font-weight: 600; }
.dgc-note-close  { color: #b45309; font-weight: 500; }
.dgc-note-sp     { visibility: hidden; }

/* ── Total (footer) ── */
.dgc-total {
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.04), rgba(139, 92, 246, 0.04));
  border-radius: 8px;
  padding: 10px 14px;
}
.dgc-total .dgc-val { font-size: 1.08rem; }
.dgc-total .dgc-bar { height: 7px; }

/* ── Dark mode ── */
:global(.dark) .dgc-val-h  { color: #6ee7b7; }
:global(.dark) .dgc-val-ia { color: #c4b5fd; }
:global(.dark) .dgc-de       { color: #6b7280; }
:global(.dark) .dgc-meta-num { color: #94a3b8; }

:global(.dark) .dgc-pct-green   { background: rgba(16, 185, 129, 0.25); color: #6ee7b7; }
:global(.dark) .dgc-pct-yellow  { background: rgba(245, 158, 11, 0.25); color: #fcd34d; }
:global(.dark) .dgc-pct-red     { background: rgba(239, 68, 68, 0.25);  color: #fca5a5; }
:global(.dark) .dgc-pct-nometa  { background: rgba(148, 163, 184, 0.2); color: #a1a1aa; }

:global(.dark) .dgc-bar { background: rgba(148, 163, 184, 0.2); }
:global(.dark) .dgc-green  .dgc-bar-fill { background: linear-gradient(90deg, #34d399, #10b981); }
:global(.dark) .dgc-yellow .dgc-bar-fill { background: linear-gradient(90deg, #fbbf24, #f59e0b); }
:global(.dark) .dgc-red    .dgc-bar-fill { background: linear-gradient(90deg, #f87171, #ef4444); }
:global(.dark) .dgc-nometa .dgc-bar-fill { background: rgba(148, 163, 184, 0.5); }

:global(.dark) .dgc-note-nometa { color: #71717a; }
:global(.dark) .dgc-note-real   { color: #a1a1aa; }
:global(.dark) .dgc-note-ok     { color: #6ee7b7; }
:global(.dark) .dgc-note-close  { color: #fcd34d; }

:global(.dark) .dgc-total {
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.1), rgba(139, 92, 246, 0.1));
}
</style>
